import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {authHeader} from '../../utils/headers';

const API_BASE = import.meta.env.VITE_APIURL || '';

const membres = (response) => {
    if (response && Array.isArray(response['hydra:member'])) return response['hydra:member'];
    return Array.isArray(response) ? response : [];
};

/**
 * TOUR-03 — simulateur de découpage (bêta). Lecture par read models (ADR-002 api) :
 *  - GET  /simulations/scenarios?patrimoine=  → { scenarios[] (coût croissant), enCours[] }
 *  - GET  /simulations/{id}                    → statut, parametres, totaux, journees (résumé), nonAffectees
 *  - GET  /simulations/{id}/journees/{date}    → visites dans l'ordre + points de carte
 *  - POST /simulations (202)                   → scénario `pending`, calculé en asynchrone
 *  - POST /simulations/calendriers (multipart) → import du calendrier de collecte (tout ou rien)
 * Lot 4 (atelier) :
 *  - GET  /simulations/calques?patrimoine=     → PAV + passages de collecte (calques collecte, sans calcul)
 *  - GET  /simulations/{id}/calques            → une couche par journée + non affectées (409 tant que ≠ done)
 *  - POST /simulations/{id}/enregistrer        → le brouillon `done` rejoint la table (corps `{libelle}` facultatif, idempotent)
 * Lot 5 :
 *  - GET  /simulations/calibration?patrimoine=&journees=  → proposition de curseurs + source (lecture seule, jamais appliquée seule)
 *  - GET  /simulations/{id}/export                        → JSON autoportant (texte brut, téléchargé par le composant ; 409 si ≠ done)
 *  - POST /simulations/import (multipart `file`)          → scénario enregistré, calculé sur le snapshot embarqué (422 patrimoine inconnu)
 * Lot 6 :
 *  - GET  /simulations/{id}/recap                         → récap de campagne (semaines × opérateurs, totaux ; 409 si ≠ done ou calculé avant le coût RH réel)
 * Lot 7 :
 *  - GET  /simulations/erreur?patrimoine=                 → erreur mesurée sur les campagnes passées (épisodes, médianes, typologie, marge affichée)
 *  - POST /simulations/{id}/sensibilite (202)             → lance l'analyse de sensibilité (asynchrone ; 409 si le scénario n'est pas `done`)
 *  - GET  /simulations/{id}/sensibilite                   → { statut en_cours|done|failed, reference, lignes[], erreur } ; 409 si jamais lancée
 * Lot 8 :
 *  - GET  /simulations/{id}/journees/{date}/trace         → { traces[] : equipe, methode routier|vol_oiseau, geometrie GeoJSON, distanceKm, kmJournee }
 * Un POST /simulations crée un BROUILLON (`enregistre: false`), absent de la table tant qu'il n'est pas enregistré.
 * Une simulation n'écrit jamais dans les circuits, campagnes ni tournées (R-TOUR-03-7).
 * Feedback et redirection restent dans les composants (CLAUDE.md, règle 3).
 */
export const simulationsApi = createApi({
    reducerPath: 'simulationsApi',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE,
        prepareHeaders: (headers) => {
            const token = authHeader();
            if (typeof token === 'string' && token) {
                headers.set('authorization', token);
            }
            headers.set('Accept', 'application/ld+json');
            return headers;
        },
    }),
    tagTypes: ['Scenarios', 'Simulation', 'Calendrier', 'Calques', 'Sensibilite'],
    endpoints: (builder) => ({
        getScenarios: builder.query({
            query: (patrimoine) => `/simulations/scenarios?patrimoine=${encodeURIComponent(patrimoine)}`,
            providesTags: (result, error, patrimoine) => [{type: 'Scenarios', id: String(patrimoine)}],
        }),
        getSimulation: builder.query({
            query: (id) => `/simulations/${id}`,
            providesTags: (result, error, id) => [{type: 'Simulation', id: String(id)}],
        }),
        getRecap: builder.query({
            query: (id) => `/simulations/${id}/recap`,
            providesTags: (result, error, id) => [{type: 'Simulation', id: String(id)}],
        }),
        // Lot 7 : erreur mesurée (R-TOUR-03-9), lecture seule ; aucun recalcul côté front.
        getErreur: builder.query({
            query: (patrimoine) => `/simulations/erreur?patrimoine=${encodeURIComponent(patrimoine)}`,
        }),
        getSensibilite: builder.query({
            query: (id) => `/simulations/${id}/sensibilite`,
            providesTags: (result, error, id) => [{type: 'Sensibilite', id: String(id)}],
        }),
        // Corps vide : la relance recalcule. Invalide la lecture (le polling reprend sur `en_cours`).
        lancerSensibilite: builder.mutation({
            query: (id) => ({url: `/simulations/${id}/sensibilite`, method: 'POST', headers: {'Content-Type': 'application/ld+json; charset=utf-8'}, body: '{}'}),
            invalidatesTags: (result, error, id) => [{type: 'Sensibilite', id: String(id)}, {type: 'Simulation', id: String(id)}],
        }),
        getJournee: builder.query({
            query: ({id, date}) => `/simulations/${id}/journees/${date}`,
        }),
        // Lot 8 : tracé de la journée (affichage seul), calculé à la demande par l'API ; repli annoncé par `methode`, jamais une erreur.
        getTraceJournee: builder.query({
            query: ({id, date}) => `/simulations/${id}/journees/${date}/trace`,
        }),
        // Sert au bandeau calendrier : pas de route de comptage côté API, on lit les disponibilités sur 1 jour
        // (un `pavs` vide en mode contraint = aucun calendrier). `contraint=true` est le défaut de l'API.
        getDisponibilites: builder.query({
            query: ({patrimoine, du, au, contraint = true, tolerances}) => {
                const q = new URLSearchParams({patrimoine: String(patrimoine), du, au, contraint: contraint ? 'true' : 'false'});
                if (tolerances) q.set('tolerances', tolerances);
                return `/simulations/disponibilites?${q.toString()}`;
            },
            providesTags: (result, error, {patrimoine}) => [{type: 'Calendrier', id: String(patrimoine)}],
        }),
        // Lot API 2c : { present, lignes, conteneursCouverts, conteneursPatrimoine, flux }. 404 si l'API n'est pas à jour.
        getCalendrier: builder.query({
            query: (patrimoine) => `/simulations/calendriers?patrimoine=${encodeURIComponent(patrimoine)}`,
            providesTags: (result, error, patrimoine) => [{type: 'Calendrier', id: String(patrimoine)}],
        }),
        createSimulation: builder.mutation({
            query: (body) => ({
                url: '/simulations',
                method: 'POST',
                headers: {'Content-Type': 'application/ld+json; charset=utf-8'},
                body: JSON.stringify(body),
            }),
            invalidatesTags: (result, error, body) => [{type: 'Scenarios', id: String(body?.patrimoine)}],
        }),
        getCalquesCollecte: builder.query({
            query: (patrimoine) => `/simulations/calques?patrimoine=${encodeURIComponent(patrimoine)}`,
            providesTags: (result, error, patrimoine) => [{type: 'Calendrier', id: String(patrimoine)}],
        }),
        getCalquesTournees: builder.query({
            query: (id) => `/simulations/${id}/calques`,
            providesTags: (result, error, id) => [{type: 'Calques', id: String(id)}],
        }),
        // Corps facultatif : sans libellé, on n'envoie rien (l'API garde le libellé du brouillon). 409 si ≠ done, 422 libellé invalide.
        enregistrerSimulation: builder.mutation({
            query: ({id, libelle}) => ({
                url: `/simulations/${id}/enregistrer`,
                method: 'POST',
                headers: {'Content-Type': 'application/ld+json; charset=utf-8'},
                body: libelle != null && String(libelle).trim() !== '' ? JSON.stringify({libelle: String(libelle).trim()}) : undefined,
            }),
            invalidatesTags: (result, error, {id}) => [
                {type: 'Simulation', id: String(id)},
                ...(result?.patrimoine?.id != null ? [{type: 'Scenarios', id: String(result.patrimoine.id)}] : []),
            ],
        }),
        // Lot 5 : lecture seule, rien n'est appliqué ni mis en cache (une calibration reflète l'historique du moment).
        getCalibration: builder.query({
            query: ({patrimoine, journees = 10}) => `/simulations/calibration?patrimoine=${encodeURIComponent(patrimoine)}&journees=${encodeURIComponent(journees)}`,
        }),
        // Mutation (et non query) : un export est une action ponctuelle, jamais mise en cache ; le texte brut est téléchargé tel quel.
        exporterSimulation: builder.mutation({
            query: (id) => ({url: `/simulations/${id}/export`, responseHandler: 'text'}),
        }),
        // multipart : ne pas forcer le Content-Type. La réponse est le scénario créé (statut pending) : la liste le suit par polling.
        importerSimulation: builder.mutation({
            query: (form) => ({url: '/simulations/import', method: 'POST', body: form}),
            invalidatesTags: ['Scenarios'],
        }),
        // multipart : surtout ne pas forcer le Content-Type (le navigateur pose la frontière).
        importCalendrier: builder.mutation({
            query: ({patrimoine, file}) => {
                const form = new FormData();
                form.append('patrimoine', String(patrimoine));
                form.append('file', file);
                return {url: '/simulations/calendriers', method: 'POST', body: form};
            },
            invalidatesTags: (result, error, {patrimoine}) => [{type: 'Calendrier', id: String(patrimoine)}],
        }),
        // Sélecteurs (lecture seule)
        getPatrimoinesSimulation: builder.query({
            query: () => '/patrimoines?groups[]=patrimoine:list',
            transformResponse: membres,
        }),
        getAgencesSimulation: builder.query({
            query: () => '/agences',
            transformResponse: membres,
        }),
    }),
});

export const {
    useGetScenariosQuery,
    useGetSimulationQuery,
    useGetRecapQuery,
    useGetJourneeQuery,
    useGetTraceJourneeQuery,
    useGetErreurQuery,
    useGetSensibiliteQuery,
    useLancerSensibiliteMutation,
    useGetDisponibilitesQuery,
    useGetCalendrierQuery,
    useGetCalquesCollecteQuery,
    useGetCalquesTourneesQuery,
    useEnregistrerSimulationMutation,
    useCreateSimulationMutation,
    useImportCalendrierMutation,
    useLazyGetCalibrationQuery,
    useExporterSimulationMutation,
    useImporterSimulationMutation,
    useGetPatrimoinesSimulationQuery,
    useGetAgencesSimulationQuery,
} = simulationsApi;
